import React from "react";
import {
  Button,
  ListItem,
  MenuItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { DeleteAttempt } from "./DeleteAttempt";
import { DeleteCertification } from "./DeleteCertification";
import { DeleteCourse } from "./DeleteCourse";
import { OdysseyUser } from "@utils";
import { ProgressList } from "./ProgressList";
import { graphql, useStaticQuery } from "gatsby";

type ResetProgressProps = {
  user: OdysseyUser;
};

export function ResetProgress({ user }: ResetProgressProps) {
  const { isOpen, onClose, onOpen } = useDisclosure();

  const data = useStaticQuery<Queries.ListCourseTasksQuery>(graphql`
    query ListCourseTasks {
      allCourse {
        nodes {
          slug
          title
          lessons {
            tasks {
              id
            }
          }
        }
      }
    }
  `);

  const courses = user.courses ?? [];
  const attempts = user.attempts ?? [];

  return (
    <>
      <MenuItem onClick={onOpen}>Reset progress...</MenuItem>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>Reset progress</ModalHeader>
          <ModalBody as={Stack} spacing="4">
            <Text>
              All of your saved progress is listed below. Press the delete
              button to the right of each item to clear that progress from the
              staging database.
            </Text>
            {courses.length > 0 && (
              <ProgressList title="Courses">
                {courses.map((course) => {
                  const localCourse = data.allCourse.nodes.find(
                    (node) => node.slug === course.id
                  );

                  if (!localCourse) {
                    return null;
                  }

                  const courseTasks = localCourse.lessons.flatMap((lesson) =>
                    lesson.tasks.map((task) => task.id)
                  );

                  const userTasks = user.tasks ?? [];
                  const taskIds = userTasks
                    .filter((task) => courseTasks.includes(task.id))
                    .map((task) => task.id);

                  return (
                    <ListItem
                      key={course.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span>{localCourse.title}</span>
                      <DeleteCourse
                        variables={{
                          userId: user.id,
                          courseId: course.id,
                          taskIds,
                        }}
                      />
                    </ListItem>
                  );
                })}
              </ProgressList>
            )}
            {attempts.length > 0 && (
              <ProgressList title="Exam attempts">
                {attempts.map((attempt) => {
                  return (
                    <ListItem
                      key={attempt.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span>
                        {attempt.testId} (
                        {new Date(attempt.startedAt).toLocaleDateString()})
                      </span>
                      <DeleteAttempt
                        variables={{
                          userId: user.id,
                          attemptId: attempt.id,
                        }}
                      />
                    </ListItem>
                  );
                })}
              </ProgressList>
            )}
            {user.certifications.length > 0 && (
              <ProgressList title="Certifications">
                {user.certifications.map((certification) => {
                  return (
                    <ListItem
                      key={certification.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <span>{certification.certificationId}</span>
                      <DeleteCertification
                        variables={{
                          userId: user.id,
                          certId: certification.id,
                        }}
                      />
                    </ListItem>
                  );
                })}
              </ProgressList>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
